/**
 * Findings page with filterable list and detail/remediation side panel.
 */

import { useState } from 'react';
import { useFindings, useDeleteFinding, useBulkDeleteFindings } from '@/hooks/useFindings';
import { useAuth } from '@/hooks/useAuth';
import { exportFindingsCsv } from '@/api/reports';
import FindingList from '@/components/findings/FindingList';
import FindingDetail from '@/components/findings/FindingDetail';
import RemediationPanel from '@/components/findings/RemediationPanel';
import Modal from '@/components/common/Modal';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import type { Finding } from '@/types/finding';

export default function FindingsPage() {
  const { canManageFindings } = useAuth();
  const { data: findings, isLoading } = useFindings();
  const [selectedFinding, setSelectedFinding] = useState<Finding | null>(null);

  const deleteFinding = useDeleteFinding();
  const bulkDeleteFindings = useBulkDeleteFindings();

  function handleDelete(id: string) {
    deleteFinding.mutate(id, {
      onSuccess: () => {
        if (selectedFinding?.id === id) setSelectedFinding(null);
      },
    });
  }

  function handleBulkDelete(ids: string[]) {
    bulkDeleteFindings.mutate(ids, {
      onSuccess: () => {
        if (selectedFinding && ids.includes(selectedFinding.id)) {
          setSelectedFinding(null);
        }
      },
    });
  }

  async function handleExportCsv() {
    try {
      await exportFindingsCsv();
    } catch {
      alert('Failed to export CSV.');
    }
  }

  if (isLoading) return <LoadingSpinner label="Loading findings..." />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Findings</h1>
        <button onClick={handleExportCsv} className="btn-secondary">
          Export CSV
        </button>
      </div>

      <FindingList
        findings={findings ?? []}
        onSelect={(f) => setSelectedFinding(f)}
        canDelete={canManageFindings}
        onDelete={handleDelete}
        onBulkDelete={handleBulkDelete}
        isDeleting={deleteFinding.isPending || bulkDeleteFindings.isPending}
      />

      {/* Finding detail modal */}
      <Modal
        isOpen={selectedFinding !== null}
        onClose={() => setSelectedFinding(null)}
        title="Finding Details"
        size="xl"
      >
        {selectedFinding && (
          <div className="space-y-6">
            <FindingDetail finding={selectedFinding} />
            <RemediationPanel
              finding={selectedFinding}
              canManage={canManageFindings}
              onStatusUpdated={(updated) => setSelectedFinding(updated)}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
